import { Delegate } from './delegate.js';

if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      /** @type {MutationObserver|null} */
      kbiteNotifyObserver = null;

      /** @type {HTMLElement|null} */
      kbiteNotifyObservedForm = null;

      /** @type {number|null} */
      _kbiteNotifySyncRaf = null;

      /** @type {boolean} When true, {@link ProductInfo#waitForKbiteButton} stops (e.g. after disconnect). */
      _notifyAbortWait = false;

      constructor() {
        super();
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateMedia(this.currentVariant);
        }
        this.initKbiteNotifyObserver();
        this.initNotifyTrigger();
        this.maybeOpenNotifyFromUrl();
      }

      /**
       * Auto-open notify modal when arriving from a product card (`?open_notify=1`).
       *
       * @returns {void}
       */
      maybeOpenNotifyFromUrl() {
        if (this.dataset.viewMode === 'quick-view') return;
        if (new URLSearchParams(window.location.search).get('open_notify') !== '1') return;

        const url = new URL(window.location.href);
        url.searchParams.delete('open_notify');
        window.history.replaceState({}, '', url.toString());

        const productUrl = this.dataset.url?.split('?')[0] || '';
        const variantInput = this.querySelector('.product-variant-id, input[name="id"]');
        const context = {
          productId: this.dataset.productId || '',
          productHandle: this.dataset.productHandle || productUrl.match(/\/products\/([^/?#]+)/)?.[1] || '',
          variantId: variantInput?.value || '',
          productUrl,
        };

        requestAnimationFrame(() => {
          import('./product-card-notify.js').then(({ openNotifyModal }) => {
            openNotifyModal(context);
          });
        });
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.kbiteNotifyObserver?.disconnect();
        this._notifyAbortWait = true;
        if (this._kbiteNotifySyncRaf != null) {
          cancelAnimationFrame(this._kbiteNotifySyncRaf);
          this._kbiteNotifySyncRaf = null;
        }
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        const sid = this.sectionId;
        return (
          this.querySelector(`#product-form-${sid}`) ||
          this.querySelector('form.main-product-form[is="product-form"]') ||
          this.querySelector('form[is="product-form"]')
        );
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            this.syncKbiteNotifyButtonState();
            return;
          }

          this.updateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false, matchAll = false) => {
            const source =
              html.getElementById(`${id}-${this.sectionId}`) ||
              html.querySelector(`[id^="${id}-${this.sectionId}-"]`);
            const destinations = matchAll
              ? this.querySelectorAll(
                  `[id="${id}-${this.dataset.section}"], [id^="${id}-${this.dataset.section}-"]`
                )
              : [this.querySelector(`#${id}-${this.dataset.section}`)].filter(Boolean);

            destinations.forEach((destination) => {
              if (!source) return;
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            });
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('ProductSubmitPrice', ({ innerHTML }) => innerHTML.trim() === '');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '', true);
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );
          this.syncKbiteNotifyButtonState();

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = this.productForm;
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonLabel = productForm.querySelector('.product-form__submit-label');
        const addButtonPrice = document.getElementById(`ProductSubmitPrice-${this.dataset.section}`);
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text && addButtonLabel) addButtonLabel.textContent = text;
          addButtonPrice?.classList.add('hidden');
        } else {
          addButton.removeAttribute('disabled');
          addButton.removeAttribute('hidden');
          addButton.classList.remove('product-form__submit--replaced-by-notify');
          if (addButtonLabel) addButtonLabel.textContent = FoxTheme.variantStrings.addToCart;
          addButtonPrice?.classList.remove('hidden');
        }

        if (!modifyClass) return;
      }

      /**
       * Watch the main product form for Kbite / disabled changes and keep notify vs ATC in sync.
       *
       * @returns {void}
       */
      initKbiteNotifyObserver() {
        const productForm = this.productForm;
        if (!productForm) return;

        this.syncKbiteNotifyButtonState();
        this.kbiteNotifyObservedForm = productForm;
        /* Observe the main form only (not full <product-info>); subtree catches Kbite injection. RAF debounces class churn from variant UI. */
        this.kbiteNotifyObserver = new MutationObserver(() => this.scheduleSyncKbiteNotifyButtonState());
        this.kbiteNotifyObserver.observe(productForm, {
          childList: true,
          subtree: true,
          attributes: true,
          /* Omit "hidden" — we toggle it on add/notify; observing it retriggers sync in a loop */
          attributeFilter: ['class', 'aria-hidden', 'disabled'],
        });
      }

      /**
       * Debounce notify UI sync to one animation frame.
       *
       * @returns {void}
       */
      scheduleSyncKbiteNotifyButtonState() {
        if (this._kbiteNotifySyncRaf != null) return;
        this._kbiteNotifySyncRaf = requestAnimationFrame(() => {
          this._kbiteNotifySyncRaf = null;
          this.syncKbiteNotifyButtonState();
        });
      }

      /**
       * Register notify trigger state (modal opens via `product-card-notify.js`).
       *
       * @returns {void}
       */
      initNotifyTrigger() {
        this._notifyAbortWait = false;
      }

      /**
       * Search scopes where Kbite may inject its widget (form, wrapper, product-info).
       *
       * @returns {ParentNode[]}
       */
      getKbiteNotifySearchScopes() {
        const scopes = [];
        const seen = new Set();

        const addScope = (scope) => {
          if (!scope || seen.has(scope)) return;
          seen.add(scope);
          scopes.push(scope);
        };

        const productForm = this.productForm;
        addScope(productForm);
        addScope(productForm?.closest('.product-form'));
        addScope(this);
        addScope(this.closest('quick-view-modal'));

        return scopes;
      }

      /**
       * @param {ParentNode} scope
       * @param {string} selector
       * @returns {HTMLElement|null}
       */
      static queryKbiteWidget(scope, selector) {
        if (!scope?.querySelector) return null;

        const direct = scope.querySelector(selector);
        if (direct && ProductInfo.isKbiteWidgetNode(direct)) return direct;

        const hosts = scope.querySelectorAll?.('*') || [];
        for (let i = 0; i < hosts.length; i++) {
          const host = hosts[i];
          if (!host.shadowRoot) continue;
          const shadowMatch = ProductInfo.queryKbiteWidget(host.shadowRoot, selector);
          if (shadowMatch) return shadowMatch;
        }

        return null;
      }

      /**
       * @param {Element} node
       * @returns {boolean}
       */
      static isKbiteWidgetNode(node) {
        return (
          node instanceof HTMLElement &&
          !node.matches('[data-action="open-kbite-notify"]') &&
          !node.closest('[data-action="open-kbite-notify"]')
        );
      }

      /**
       * Locate the Kbite widget root (`.gw-button-widget`), not the theme notify trigger.
       *
       * @returns {HTMLElement|null}
       */
      findKbiteNotifyButton() {
        const selectors = [
          '.gw-button-widget.gw-button-widget-v2',
          '.gw-button-widget',
          '.product-form__buttons .gw-button-widget',
        ];

        for (const scope of this.getKbiteNotifySearchScopes()) {
          for (let i = 0; i < selectors.length; i++) {
            const el = ProductInfo.queryKbiteWidget(scope, selectors[i]);
            if (el) return el;
          }
        }

        return null;
      }

      /**
       * @param {HTMLElement} widget
       * @returns {HTMLElement}
       */
      resolveKbiteClickTarget(widget) {
        return (
          widget.querySelector('button:not([disabled])') ||
          widget.querySelector('[role="button"]') ||
          widget.querySelector('a[href]') ||
          widget
        );
      }

      /**
       * Proxy a user click to Kbite's hidden control (restores pointer-events briefly).
       *
       * @param {HTMLElement} widget
       * @returns {boolean}
       */
      dispatchKbiteClick(widget) {
        const clickTarget = this.resolveKbiteClickTarget(widget);
        const restore = [];

        for (let node = clickTarget; node; node = node.parentElement) {
          restore.push([node, node.style.pointerEvents]);
          node.style.pointerEvents = 'auto';
          if (node === widget) break;
        }

        clickTarget.dispatchEvent(
          new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
        );
        clickTarget.click();

        requestAnimationFrame(() => {
          restore.forEach(([node, value]) => {
            if (value) node.style.pointerEvents = value;
            else node.style.removeProperty('pointer-events');
          });
        });

        return true;
      }

      /**
       * Wait until a Kbite notify control exists in the form subtree or timeout.
       *
       * @param {number} [timeoutMs=4000]
       * @returns {Promise<HTMLElement|null>}
       */
      async waitForKbiteButton(timeoutMs = 4000) {
        const start = performance.now();
        while (!this._notifyAbortWait && performance.now() - start < timeoutMs) {
          const btn = this.findKbiteNotifyButton();
          if (btn) return btn;
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
        return null;
      }

      /**
       * Programmatically open Kbite’s modal via its hidden button.
       *
       * @returns {boolean} true if a click was dispatched
       */
      openKbiteNotifyModal() {
        const kbiteBtn = this.findKbiteNotifyButton();
        if (!kbiteBtn) return false;
        return this.dispatchKbiteClick(kbiteBtn);
      }

      /**
       * Toggle theme notify vs main ATC based on stock + Kbite presence.
       *
       * @returns {void}
       */
      syncKbiteNotifyButtonState() {
        const productForm = this.productForm;
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const quantityWrapper = productForm.querySelector('.product-form__quantity');
        const customNotify = productForm.querySelector('[data-action="open-kbite-notify"]');
        if (!addButton) return;

        const kbiteNotifyFeatureOn = this.dataset.kbiteNotify !== 'false';
        const addDisabled = addButton.hasAttribute('disabled');
        const showCustomNotify = kbiteNotifyFeatureOn && Boolean(customNotify) && addDisabled;

        const showAddButton = () => {
          addButton.removeAttribute('hidden');
          addButton.classList.remove('product-form__submit--replaced-by-notify');
        };
        const hideAddButton = () => {
          addButton.removeAttribute('hidden');
          addButton.classList.add('product-form__submit--replaced-by-notify');
        };
        const showNotifyButton = () => {
          customNotify?.removeAttribute('hidden');
        };
        const hideNotifyButton = () => {
          customNotify?.setAttribute('hidden', '');
        };

        if (showCustomNotify) {
          showNotifyButton();
          hideAddButton();
        } else {
          hideNotifyButton();
          showAddButton();
        }

        if (quantityWrapper) {
          if (showCustomNotify) {
            quantityWrapper.style.removeProperty('min-width');
            quantityWrapper.style.removeProperty('width');
            quantityWrapper.style.removeProperty('flex');
          } else {
            quantityWrapper.style.setProperty('min-width', '0', 'important');
            quantityWrapper.style.setProperty('width', 'auto', 'important');
            quantityWrapper.style.setProperty('flex', '0 1 auto', 'important');
          }
        }

        this.syncStickyKbiteNotifyButtons();
      }

      /**
       * Mirror main PDP notify vs ATC visibility onto sticky bars for the same product.
       *
       * @returns {void}
       */
      syncStickyKbiteNotifyButtons() {
        const productForm = this.productForm;
        if (!productForm) return;

        const mainAdd = productForm.querySelector('[name="add"]');
        const mainNotify = productForm.querySelector('[data-action="open-kbite-notify"]');
        if (!mainAdd) return;

        const showStickyNotify = mainNotify && !mainNotify.hasAttribute('hidden');
        const mainAddReplaced = mainAdd.classList.contains('product-form__submit--replaced-by-notify');

        document.querySelectorAll('sticky-atc-bar, sticky-atc-bar-minimalist').forEach((bar) => {
          if (String(bar.dataset.productId) !== String(this.productId)) return;

          const stickyAdd = bar.querySelector('[name="add"]');
          const stickyNotify = bar.querySelector('[data-action="open-kbite-notify"]');
          if (!stickyAdd) return;

          if (showStickyNotify && stickyNotify) {
            stickyNotify.classList.add('btn', 'btn--secondary');
            stickyNotify.classList.remove('btn--primary', 'product-form__submit');
            stickyNotify.removeAttribute('hidden');
            stickyAdd.removeAttribute('hidden');
            stickyAdd.classList.add('product-form__submit--replaced-by-notify');
            if (mainAdd.hasAttribute('disabled')) {
              stickyAdd.setAttribute('disabled', 'disabled');
            }
          } else {
            if (stickyNotify) {
              stickyNotify.setAttribute('hidden', '');
            }
            stickyAdd.classList.toggle('product-form__submit--replaced-by-notify', mainAddReplaced);
            if (mainAdd.hasAttribute('disabled')) {
              stickyAdd.setAttribute('disabled', 'disabled');
              if (!mainAddReplaced) {
                stickyAdd.setAttribute('hidden', '');
              } else {
                stickyAdd.removeAttribute('hidden');
              }
            } else {
              stickyAdd.removeAttribute('disabled');
              stickyAdd.removeAttribute('hidden');
            }
          }
        });
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventoryElements = this.querySelectorAll(
          `[id="Inventory-${this.dataset.section}"], [id^="Inventory-${this.dataset.section}-"]`
        );
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        inventoryElements.forEach((inventory) => inventory.classList.add('hidden'));
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
